import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, BookOpen, ClipboardCheck, Activity, AlertTriangle, CheckCircle2, TrendingUp, Clock, Shield, Send, Mail, ShieldAlert } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [emailLog, setEmailLog] = useState([]);

  useEffect(() => { loadDashboard(); loadEmailLog(); }, []);

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/admin`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error('Dashboard load failed'); }
    setLoading(false);
  };

  const loadEmailLog = async () => {
    try {
      const r = await axios.get(`${API}/alerts/email-log?limit=10`, { withCredentials: true });
      setEmailLog(r.data);
    } catch { /* ignore */ }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/alerts/weekly-digest`, {}, { withCredentials: true });
      const sentCount = r.data.sent.filter(s => s.status === 'sent').length;
      const failedCount = r.data.sent.filter(s => s.status === 'failed').length;
      const mockedCount = r.data.sent.filter(s => s.status === 'mocked').length;
      let msg = `Digest dispatched to ${r.data.sent.length} recipient(s).`;
      if (sentCount) msg += `\n✓ ${sentCount} delivered via Resend`;
      if (failedCount) msg += `\n✗ ${failedCount} failed (check email log for details — likely needs verified domain)`;
      if (mockedCount) msg += `\n◦ ${mockedCount} mocked (no provider configured)`;
      alert(msg);
      loadEmailLog();
    } catch { alert('Failed to send digest'); }
    setSendingDigest(false);
  };

  if (loading) return <div className="min-h-screen bg-background"><Header /><div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#3fa758] border-t-transparent rounded-full" /></div></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground" data-testid="dashboard-title">Management Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Overview of training, compliance, and system health</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/logins" data-testid="goto-login-activity"
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border"
              style={{ borderColor: '#DC262640', color: '#DC2626', backgroundColor: '#DC26260A' }}>
              <ShieldAlert className="w-4 h-4" /> Login Activity
            </Link>
            <button data-testid="send-digest-btn" onClick={sendDigest} disabled={sendingDigest}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-xl disabled:opacity-40" style={{ backgroundColor: '#3fa758' }}>
              <Send className="w-4 h-4" /> {sendingDigest ? 'Sending...' : 'Send weekly digest'}
            </button>
          </div>
        </div>

        {data && (
          <>
            {/* Top Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats">
              <StatCard icon={Users} label="Active Staff" value={data.total_staff} color="#3fa758" />
              <StatCard icon={BookOpen} label="Training Modules" value={data.total_modules} color="#3fa758" />
              <StatCard icon={CheckCircle2} label="Completed Training" value={data.training_completions} color="#16A34A" />
              <StatCard icon={ClipboardCheck} label="Active SOPs" value={data.sop_count} color="#7C3AED" />
            </div>

            {/* Alert Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className={`border rounded-xl p-5 ${data.sop_overdue > 0 ? 'border-red-500/50 bg-red-500/5' : 'border-border bg-card'}`}>
                <div className="flex items-center gap-3 mb-1">
                  <AlertTriangle className={`w-5 h-5 ${data.sop_overdue > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <span className="text-xs font-medium text-muted-foreground uppercase">Overdue SOPs</span>
                </div>
                <p className={`font-['Outfit'] text-3xl font-bold ${data.sop_overdue > 0 ? 'text-red-500' : 'text-foreground'}`}>{data.sop_overdue}</p>
              </div>
              <div className={`border rounded-xl p-5 ${data.sop_pending > 0 ? 'border-amber-500/50 bg-amber-500/5' : 'border-border bg-card'}`}>
                <div className="flex items-center gap-3 mb-1">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">Pending SOPs</span>
                </div>
                <p className="font-['Outfit'] text-3xl font-bold text-amber-500">{data.sop_pending}</p>
              </div>
              <div className={`border rounded-xl p-5 ${data.mbs_changes > 0 ? 'border-blue-500/50 bg-blue-500/5' : 'border-border bg-card'}`}>
                <div className="flex items-center gap-3 mb-1">
                  <Activity className="w-5 h-5 text-blue-500" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">MBS Fee Changes</span>
                </div>
                <p className="font-['Outfit'] text-3xl font-bold text-blue-500">{data.mbs_changes}</p>
              </div>
            </div>

            {/* Latest Audit */}
            {data.latest_audit && (
              <div className="bg-card border border-border rounded-xl p-6 mb-8" data-testid="dashboard-audit">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-['Outfit'] text-base font-semibold text-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4" style={{ color: '#7C3AED' }} /> Latest External Audit
                  </h3>
                  <span className={`text-xl font-bold ${data.latest_audit.compliance_score >= 80 ? 'text-green-500' : data.latest_audit.compliance_score >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                    {data.latest_audit.compliance_score}%
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{data.latest_audit.auditor_company} — {data.latest_audit.audit_date}</p>
                <p className="text-sm text-foreground/80 mt-2">{data.latest_audit.recommendations}</p>
                <p className="text-xs mt-2" style={{ color: 'var(--i3-teal)' }}>Next audit: {data.latest_audit.next_audit_date}</p>
              </div>
            )}

            {/* Recent Activity */}
            {data.recent_activity?.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-6" data-testid="dashboard-activity">
                <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" style={{ color: 'var(--i3-teal)' }} /> Recent Training Activity
                </h3>
                <div className="space-y-2">
                  {data.recent_activity.map((a, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <span className="text-sm font-medium text-foreground">{a.user_name || 'Staff'}</span>
                        <span className="text-xs text-muted-foreground ml-2">completed step {a.step_index + 1} in {a.module_id}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{a.completed_at ? new Date(a.completed_at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email log */}
            {emailLog.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-6 mt-6" data-testid="dashboard-email-log">
                <h3 className="font-['Outfit'] text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Mail className="w-4 h-4" style={{ color: 'var(--i3-teal)' }} /> Recent emails
                  {emailLog[0]?.provider === 'mock' && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">MOCKED</span>}
                </h3>
                <div className="space-y-2">
                  {emailLog.map((e, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-xs">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-foreground">{e.subject}</span>
                        <span className="text-muted-foreground ml-2">→ {e.to_email}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${e.status === 'sent' ? 'bg-green-500/10 text-green-600' : e.status === 'mocked' ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-600'}`}>
                          {e.status}
                        </span>
                        <span className="text-muted-foreground">{new Date(e.sent_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {emailLog[0]?.provider === 'mock' && (
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                    Email delivery is currently mocked. Add <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">RESEND_API_KEY</code> and <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">RESEND_FROM</code> to <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">backend/.env</code> and restart.
                  </p>
                )}
                {emailLog[0]?.provider === 'resend' && emailLog.some(e => e.status === 'failed' && e.error?.includes('verify a domain')) && (
                  <p className="text-xs text-amber-600 mt-3 pt-3 border-t border-border">
                    <strong>Domain not verified:</strong> Resend's test sender only delivers to your Resend account email. Verify a domain at{' '}
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>
                    {' '}to send to all staff, then update <code className="text-[10px] bg-amber-500/10 px-1.5 py-0.5 rounded">RESEND_FROM</code> in backend/.env.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-['Outfit'] text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}
